import { Box, Flex, Heading, Grid, Text, Container } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/use-tenant-context';
import { Button } from '@trashlab/sitesapp/src/organizations/piedmont/template/components/shared/button';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import Breadcrumbs from '@trashlab/sitesapp/src/organizations/piedmont/template/components/shared/breadcrumbs';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/get-variant-value';
import { Image } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/image';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/use-theme-text-color';
import { useIsMobile } from '@trashlab/sitesapp/src/organizations/piedmont/hooks/use-mobile';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const HeroSection = ({
  title,
  content,
  heroImage,
}: {
  title: string;
  content: string;
  heroImage?: string;
}) => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const { textColorClass } = useThemeTextColor();
  const isMobile = useIsMobile();

  const heroBackgroundImage = useVariantValue(
    'HOME_HERO_BACKGROUND_IMAGE',
    '/default/hero-section-background.svg',
  );

  const ctaButtonText = useVariantValue(
    'CTA_BUTTON_TEXT',
    'Explore our services',
  );

  return (
    <Container
      size="4"
      p="8"
      pt="4"
      className={cn('min-h-[200px]', 'relative overflow-hidden')}
    >
      <Box className="absolute inset-0 -z-20">
        <Image
          src={heroBackgroundImage}
          alt="Background"
          className="w-full h-full object-cover"
        />
      </Box>
      <Box className="absolute inset-0 -z-10 bg-[var(--background-color)] opacity-90" />

      {heroImage ? (
        <Grid
          columns={{ initial: '1', xs: '2' }}
          width="auto"
          justify="center"
          align="center"
          className="relative z-10"
        >
          <Box className="order-2 xs:order-1 relative flex-1 h-full min-h-[180px] xs:min-h-[250px] sm:min-h-[400px] overflow-hidden">
            <Box className="absolute inset-0 overflow-hidden">
              <Image
                src={heroImage}
                alt="Hero"
                className="w-full h-full object-cover object-center"
              />
            </Box>
          </Box>

          <Flex
            direction="column"
            className="order-1 xs:order-2 relative min-h-[200px] justify-between space-y-8"
          >
            <Breadcrumbs title={title} />
            <Flex direction="column" gap="4">
              <Heading
                size={{ initial: '8', md: '9' }}
                className={textColorClass}
                data-variable="CONTACT_TITLE"
              >
                {title ??
                  `${website?.organization?.name || "Our company's"} rental ${
                    orgAddress?.city ?? 'San Francisco'
                  }`}
              </Heading>

              <Text
                size={{ initial: '2', md: '4' }}
                className={textColorClass}
                data-variable="CONTACT_SUBTITLE"
              >
                {content}
              </Text>

              <Box className="flex justify-end">
                <Button
                  withArrow
                  scrollToDumpsterTypes
                  data-variable="CTA_BUTTON_TEXT"
                >
                  {ctaButtonText}
                </Button>
              </Box>
            </Flex>
          </Flex>
        </Grid>
      ) : (
        <Flex
          direction="column"
          className="relative z-10 min-h-[200px] justify-between space-y-8"
        >
          <Breadcrumbs title={title} />
          <Flex direction="column" gap="4">
            <Heading
              size={{ initial: '8', md: '9' }}
              className={textColorClass}
              data-variable="CONTACT_TITLE"
            >
              {title ??
                `${website?.organization?.name || "Our company's"} rental ${
                  orgAddress?.city ?? 'San Francisco'
                }`}
            </Heading>

            <Grid
              columns={{ initial: '1', xs: '2' }}
              width="auto"
              justify="center"
              align="center"
            >
              <Text
                size={{ initial: '2', md: '4' }}
                className={textColorClass}
                data-variable="CONTACT_SUBTITLE"
              >
                {content}
              </Text>

              <Box
                className={`flex items-center ${
                  isMobile ? '' : 'justify-end'
                } col-span-1 mt-4`}
              >
                <Button
                  withArrow
                  scrollToDumpsterTypes
                  data-variable="CTA_BUTTON_TEXT"
                >
                  {ctaButtonText}
                </Button>
              </Box>
            </Grid>
          </Flex>
        </Flex>
      )}
    </Container>
  );
};

export default HeroSection;
